import { APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getNoDataHTML, getWidgetDataURI, isEmptyWidgetData, apiGet, toggleWidgetTheme, normalizeWidgetTheme, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin } from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

export class TraderverseTrendingIndexes {
  private container: HTMLElement;
  private config: WidgetConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: any = null;
  private widgetResizeObserver: ResizeObserver | null = null;

  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }

    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });

    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'AAPL', value: 'AAPL' },
      { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' },
      { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' },
      { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' },
      { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' },
      { label: 'COST', value: 'COST' }
    ];
  }

  private getDefaultConfig(): Partial<WidgetConfig> {
    const symbols = this.fetchSymbols();
    return {
      headerPinned: true,
      symbol: symbols[0].value,
      theme: 'black',
      width: '100%',
      height: '400px',
      autosize: true
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
    this.createWidget();
    this.attachStyles();
    this.setupEventListeners();
    await this.setupChart(this.config.symbol!);
    this.emit('ready', {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private createWidget(): void {
    const symbolOptions = this.fetchSymbols()
      .map(s => `<option value="${s.value}" ${s.value === this.config.symbol ? 'selected' : ''}>${s.label}</option>`)
      .join('');

    const widgetHtml = `
      <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${this.config.theme === 'white' ? 'whiteMode' : ''} ${getHeaderPinStateClass(this.config)}" data-theme="${this.config.theme}">
        <div class="headTop">
          <h4 class="title stock">${this.getTitleIconSvg()}
            Trending Index
          </h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<i class="pi pi-ellipsis-v"></i></div>
        </div>

        <div class="inBody">
          <div class="optionsRow">
            <div class="item">
              <select class="symbol-dropdown">
                ${symbolOptions}
              </select>
            </div>
          </div>
          <div class="chartDv" id="keyMarticsContainer"></div>
        </div>
      </div>
    `;
    this.shadowRoot.innerHTML = widgetHtml;
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS:', err);
    }
  }

  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    dropdown?.addEventListener('change', e => {
      const value = (e.target as HTMLSelectElement).value;
      this.changeSymbol(value);
    });
    if (dropdown && this.config.symbol) dropdown.value = this.config.symbol;
  }

  private async setupChart(symbol: string): Promise<void> {
    try {
      const res = await apiGet({
        url: `${APIURL}/v1/discover/trending_indexes`,
        body: { symbol },
      });

      if (!res.ok) throw new Error('API Error');
      const data = await res.json();

      // ✅ Ensure we’re accessing correct structure
      this.currentData = data?.data ?? data;
      this.updateDisplay();

      this.emit('dataUpdate', this.currentData);
    } catch (err) {
      console.error('Error fetching chart data:', err);
    }
  }

  private updateDisplay(): void {
    const container = this.shadowRoot.querySelector('#keyMarticsContainer') as HTMLElement;
    if (!container) return;
    if (this.currentData == null) return;
    const records = this.currentData.records || [];
    if (isEmptyWidgetData(records)) {
      container.innerHTML = getNoDataHTML();
      return;
    }
    this.renderKeyMatrics(records);
  }

  private renderKeyMatrics(dataArr: any[]): void {
    const container = this.shadowRoot.querySelector('#keyMarticsContainer');
    if (!container || !Array.isArray(dataArr) || dataArr.length === 0) {
      if (container) container.innerHTML = getNoDataHTML();
      return;
    }

    const getChangeColor = (delta: string) =>
      delta === 'up' ? 'green' : delta === 'down' ? 'red' : 'gray';

    let html = `<div class="keyMetricsMain">`;

    dataArr.forEach((item) => {
      const { ticker, name, lastSale, pctChange, deltaIndicator } = item;
      const color = getChangeColor(deltaIndicator);
      html += `
        <div class="item">
          <div class="left">
            <h4>${ticker?.[0] ?? '-'}</h4>
            <p>${name ?? '-'}</p>
          </div>
          <div class="right">
            <h5>${lastSale ? lastSale.toFixed(2) : '-'}</h5>
            <span class="pctChange ${color}">
              ${pctChange > 0 ? '+' : ''}${pctChange?.toFixed(2) ?? 0}%
            </span>
          </div>
        </div>
      `;
    });

    html += `</div>`;
    container.innerHTML = html;
  }

  private changeSymbol(stockId: string, stock: any = null): void {
    changeSymbolForWidget(
      this.shadowRoot,
      this.config,
      stock,
      stockId,
      (id: any) => this.setupChart(id),
      (type: any, data: any) => this.emit(type, data)
    );
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }

  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }

  public setSymbol(symbol: string): void {
    this.changeSymbol(symbol);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    if (dropdown) dropdown.value = symbol;
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }

  public setTheme(theme: 'white' | 'black'): void {
    this.config.theme = toggleWidgetTheme(this.shadowRoot, this.config.theme!) as any;
    this.updateDisplay();
  }

  public destroy(): void {
    if (this.updateInterval) clearInterval(this.updateInterval);
    if (this.widgetResizeObserver) this.widgetResizeObserver.disconnect();
    this.eventHandlers.clear();
    this.shadowRoot.innerHTML = '';
  }

  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) {
      const evt: WidgetEvent = { type, data };
      handlers.forEach(h => h(evt));
    }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8.6 23.625C8.087 23.625 7.662 23.2 7.662 22.6875V20.1C7.662 19.5875 8.087 19.1625 8.6 19.1625C9.112 19.1625 9.537 19.5875 9.537 20.1V22.6875C9.537 23.2125 9.112 23.625 8.6 23.625Z" fill="currentColor"/>
              <path d="M15 23.625C14.487 23.625 14.062 23.2 14.062 22.6875V17.5C14.062 16.9875 14.487 16.5625 15 16.5625C15.512 16.5625 15.937 16.9875 15.937 17.5V22.6875C15.937 23.2125 15.512 23.625 15 23.625Z" fill="currentColor"/>
              <path d="M21.4 23.625C20.887 23.625 20.462 23.2 20.462 22.6875V14.9125C20.462 14.4 20.887 13.975 21.4 13.975C21.912 13.975 22.337 14.4 22.337 14.9125V22.6875C22.337 23.2125 21.925 23.625 21.4 23.625Z" fill="currentColor"/>
            </svg>`;
  }

}

declare global {
  interface Window { TraderverseTrendingIndexes: typeof TraderverseTrendingIndexes; }
}

if (typeof window !== 'undefined') {
  window.TraderverseTrendingIndexes = TraderverseTrendingIndexes;
}
